import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { authFetch } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';
import { THEME_COLORS } from '../components/Constants';
import { FaKey, FaCheckCircle, FaTimesCircle, FaEdit, FaUsers, FaChartLine, FaCogs } from 'react-icons/fa';
import { MdSettingsInputAntenna } from 'react-icons/md';
import { useNotification } from '../contexts/NotificationContext';

const formatCurrency = (amount) => {
    const number = Number(amount);
    if (isNaN(number) || number === 0) return "0,00";
    return number.toLocaleString("fr-FR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const AdminUserFilterBar = ({ filter, setFilter, themeColors, filteredCount, totalCount }) => {
    
    const handleReset = () => {
        setFilter({
            startDate: '', // Date d'inscription
            endDate: '',
            categorieId: '', // Utilisé pour le PLAN
            description: '', // Utilisé pour Nom/Email
            minAmount: '',   // Tarif min
            maxAmount: '',   // Tarif max
        });
    };

    const planOptions = [
        { id: 'TRIAL', name: 'TRIAL' },
        { id: 'STANDARD', name: 'STANDARD' },
        { id: 'PREMIUM', name: 'PREMIUM' }
    ];

    return (
        <div style={{...s.filterBar, border: `1px solid ${themeColors.primary}50`}}>
            <h4 style={{ margin: '0 0 15px 0', color: themeColors.text }}>
                Filtres Utilisateurs {(filter.description || filter.categorieId) && "💡"}
            </h4>
            
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' }}>
                {/* Recherche Nom/Email */}
                <div style={{ flex: 2, minWidth: '200px' }}>
                    <label style={s.filterLabel}>Rechercher (Nom, Email)</label>
                    <input 
                        type="text" 
                        style={s.input}
                        placeholder="Ex: Jean ou @gmail.com"
                        value={filter.description}
                        onChange={(e) => setFilter({...filter, description: e.target.value})}
                    />
                </div>

                {/* Filtre par Plan */}
                <div style={{ flex: 1, minWidth: '150px' }}>
                    <label style={s.filterLabel}>Plan d'abonnement</label>
                    <select 
                        style={s.input}
                        value={filter.categorieId}
                        onChange={(e) => setFilter({...filter, categorieId: e.target.value})}
                    >
                        <option value="">Tous les plans</option>
                        {planOptions.map(plan => (
                            <option key={plan.id} value={plan.id}>{plan.name}</option>
                        ))}
                    </select>
                </div>

                {/* Filtre par Tarif */}
                <div style={{ display: 'flex', gap: '5px', alignItems: 'flex-end' }}>
                    <div>
                        <label style={s.filterLabel}>Tarif min</label>
                        <input 
                            type="number" 
                            style={{...s.input, width: '80px'}} 
                            value={filter.minAmount}
                            onChange={(e) => setFilter({...filter, minAmount: e.target.value})}
                        />
                    </div>
                    <div>
                        <label style={s.filterLabel}>Tarif max</label>
                        <input 
                            type="number" 
                            style={{...s.input, width: '80px'}} 
                            value={filter.maxAmount}
                            onChange={(e) => setFilter({...filter, maxAmount: e.target.value})}
                        />
                    </div>
                </div>

                <button onClick={handleReset} style={s.resetButton}>Réinitialiser</button>
            </div>
            
            <p style={{ marginTop: '10px', fontSize: '0.85em', color: '#666' }}>
                Utilisateurs trouvés : <strong>{filteredCount}</strong> sur {totalCount}
            </p>
        </div>
    );
};

export default function AdminUsersPage({ updateCurrentUser }) {
    const { currentTheme } = useTheme();
    const themeColors = THEME_COLORS[currentTheme] || THEME_COLORS.blue;
    const { notify } = useNotification();

    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeSubTab, setActiveSubTab] = useState("users");
    const [systemConfig, setSystemConfig] = useState([]);

    // --- Système de Filtres ---
    const [isFilterBarVisible, setIsFilterBarVisible] = useState(false);
    const [userFilter, setUserFilter] = useState({
        startDate: '',
        endDate: '',
        categorieId: '', // Utilisé pour filtrer par PLAN
        description: '', // Utilisé pour Nom/Email
        minAmount: '',   // Utilisé pour Tarif Min
        maxAmount: '',   // Utilisé pour Tarif Max
    });

    // --- Système de Sélection (Identique au Dashboard) ---
    const [selectedUserIds, setSelectedUserIds] = useState([]);
    const selectAllRef = useRef(null);

    const loadAdminData = useCallback(async () => {
        setLoading(true);
        try {
            const [resUsers, resStats, resConfig] = await Promise.all([
                authFetch.get("/admin/users-complete"),
                authFetch.get("/admin/stats"),
                authFetch.get("/api/admin/config")
            ]);
            
            const usersData = await resUsers.json();
            const statsData = await resStats.json();
            const configData = await resConfig.json();

            if (usersData.error) {
                notify(usersData.error, "error");
                setUsers([]);
            } else {
                setUsers(Array.isArray(usersData) ? usersData : []);
            }

            if (statsData.error) {
                notify("Erreur stats: " + statsData.error, "warning");
            } else {
                setStats(statsData);
            }

            setSystemConfig(Array.isArray(configData) ? configData : []);

        } catch (err) { 
            console.error("Erreur critique chargement admin", err); 
            notify("Impossible de contacter le serveur.", "error");
            setUsers([]);
        } finally { 
            setLoading(false); 
        }
    }, [notify]);

    useEffect(() => {
        // Chargement initial
        loadAdminData();

        // 💡 Mise en place d'un polling pour voir les demandes de support arriver
        const interval = setInterval(() => {
            loadAdminData();
        }, 15000); // 15 secondes : un bon compromis performance/réactivité

        return () => clearInterval(interval);
    }, [loadAdminData]);

    // --- Logique de filtrage ---
    const filteredUsers = useMemo(() => {
        if (!Array.isArray(users) || users.length === 0) return [];

        return users.filter(u => {
            // 1. Filtre par Nom / Email (si vide, on laisse tout passer)
            const searchTerm = userFilter.description?.trim().toLowerCase() || "";
            const name = String(u.name || "").toLowerCase();
            const email = String(u.email || "").toLowerCase();
            const matchesSearch = searchTerm === "" || name.includes(searchTerm) || email.includes(searchTerm);

            // 2. Filtre par Plan (si vide, on laisse tout passer)
            const filterPlan = userFilter.categorieId || "";
            const matchesPlan = filterPlan === "" || u.plan_name === filterPlan;

            // 3. Filtre par Prix
            const price = Number(u.monthly_price || 0);
            const minPrice = userFilter.minAmount === '' ? -Infinity : parseFloat(userFilter.minAmount);
            const maxPrice = userFilter.maxAmount === '' ? Infinity : parseFloat(userFilter.maxAmount);
            const matchesPrice = price >= minPrice && price <= maxPrice;

            return matchesSearch && matchesPlan && matchesPrice;
        });
    }, [users, userFilter]);

    // --- Logique de sélection ---
    const handleSelectUser = (id) => {
        setSelectedUserIds(prev => prev.includes(id) ? prev.filter(uid => uid !== id) : [...prev, id]);
    };

    const handleSelectAll = () => {
        const allIds = filteredUsers.map(u => u.id);
        setSelectedUserIds(selectedUserIds.length < allIds.length ? allIds : []);
    };

    useEffect(() => {
        if (selectAllRef.current) {
            const allIds = filteredUsers.map(u => u.id);
            selectAllRef.current.checked = allIds.length > 0 && selectedUserIds.length === allIds.length;
            selectAllRef.current.indeterminate = selectedUserIds.length > 0 && selectedUserIds.length < allIds.length;
        }
    }, [selectedUserIds, filteredUsers]);

    // --- Actions ---
    const handleBatchUpdatePrice = async () => {
        const newPrice = window.prompt(`Définir un nouveau tarif pour les ${selectedUserIds.length} utilisateurs sélectionnés :`);
        if (newPrice !== null && !isNaN(newPrice)) {
            try {
                await authFetch.post("/api/admin/users/batch-update-price", {
                    ids: selectedUserIds,
                    monthly_price: parseFloat(newPrice)
                });
                notify("Tarifs mis à jour avec succès.", "success");
                setSelectedUserIds([]);
                loadAdminData();
            } catch (err) { notify("Erreur lors de la mise à jour.", "error"); }
        }
    };

    const updatePlan = async (userId, plan) => {
        const maxAcc = plan === 'PREMIUM' ? 99 : (plan === 'STANDARD' ? 5 : 2);
        await authFetch.put("/admin/update-subscription", {
            targetUserId: userId, plan_name: plan, max_accounts: maxAcc
        });
        loadAdminData();
    };

    const handleUpdateEmail = async (userId, oldEmail) => {
        const newEmail = window.prompt(`Modifier l'email de ${oldEmail} :`, oldEmail);
        if (newEmail && newEmail !== oldEmail) {
            await authFetch.put(`/users/update-group/${userId}`, { email: newEmail });
            loadAdminData();
        }
    };

    const handleSaveConfig = async (key, value, applyToAll) => {
        try {
            await authFetch.put("/api/admin/config", { key, value, applyToAll });
            notify(`Paramètre ${key} mis à jour.`, "success");
            loadAdminData();
        } catch (err) { notify("Erreur de sauvegarde", "error"); }
    };

    const [isWaitingForUser, setIsWaitingForUser] = useState(false);

    const handleLogAs = async (targetUserId) => {
        setIsWaitingForUser(true);
        
        try {
            const res = await authFetch.post("/api/admin/request-log-as", { targetUserId });
            if (!res.ok) {
                setIsWaitingForUser(false);
                const err = await res.json();
                return notify(err.error || "Erreur demande", "error");
            }

            notify("Demande envoyée. En attente de l'autorisation...", "info");

            let attempts = 0;
            // 💡 On stocke l'intervalle dans une variable locale pour être sûr de le killer
            const checkInterval = setInterval(async () => {
                attempts++;
                
                try {
                    // On utilise une requête directe pour éviter tout effet de bord d'authFetch sur le polling
                    const confirmRes = await authFetch.post("/api/admin/confirm-log-as", { targetUserId });
                    
                    if (confirmRes.status === 200) {
                        const confirmData = await confirmRes.json();
                        clearInterval(checkInterval); // 🛑 ARRÊT IMMÉDIAT DU POLLING
                        
                        // 1. Appliquer les nouvelles données
                        updateCurrentUser(confirmData.user);
                        
                        // 2. Nettoyer les traces de l'ancienne session
                        localStorage.removeItem('selectedPerson');
                        localStorage.removeItem('selectedAccount');

                        // 3. Rediriger brutalement pour réinitialiser l'état React propre
                        window.location.replace("/comptes"); 
                        return;
                    } 
                    
                    if (confirmRes.status !== 202) {
                        clearInterval(checkInterval);
                        setIsWaitingForUser(false);
                        notify("Session refusée ou expirée.", "warning");
                    }
                } catch (err) {
                    console.error("Polling error:", err);
                }

                if (attempts > 30) {
                    clearInterval(checkInterval);
                    setIsWaitingForUser(false);
                    notify("Délai dépassé.", "warning");
                }
            }, 5000);

        } catch (err) {
            setIsWaitingForUser(false);
            notify("Erreur réseau.", "error");
        }
    };

    if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Chargement...</div>;

    return (
        <div style={s.adminLayout}>
            {/* 1. SIDEBAR ADMIN */}
            <aside style={{ ...s.sidebar, backgroundColor: themeColors.backgroundnav }}>
                <h3 style={{ color: themeColors.primary, padding: '0 15px' }}>Admin Panel</h3>
                <nav style={s.navList}>
                    <div style={activeSubTab === 'users' ? s.navActive(themeColors) : s.navItem} onClick={() => setActiveSubTab('users')}>
                        <FaUsers /> Utilisateurs
                    </div>
                    <div style={activeSubTab === 'stats' ? s.navActive(themeColors) : s.navItem} onClick={() => setActiveSubTab('stats')}>
                        <FaChartLine /> Statistiques
                    </div>
                    <div style={activeSubTab === 'config' ? s.navActive(themeColors) : s.navItem} onClick={() => setActiveSubTab('config')}>
                        <FaCogs /> Configuration
                    </div>
                </nav>
            </aside>

            {/* 2. CONTENU PRINCIPAL */}
            <main style={s.mainContent}>
                {activeSubTab === 'users' && (
                    <div style={s.tabContentContainer}>
                        <div style={s.fixedContent}>
                            <h2 style={{ color: themeColors.secondary, margin: '0 0 15px 0' }}>Gestion des Utilisateurs</h2>

                            {/* Cartes de Stats Rapides réintégrées */}
                            <div style={s.statsGrid}>
                                <div style={s.statCard}>
                                    <span style={s.statLabel}>Utilisateurs Totaux</span>
                                    <strong style={s.statValue}>{stats?.total_users}</strong>
                                </div>
                                <div style={s.statCard}>
                                    <span style={s.statLabel}>Vérifiés</span>
                                    <strong style={{ ...s.statValue, color: 'green' }}>{stats?.verified_users}</strong>
                                </div>
                                <div style={s.statCard}>
                                    <span style={s.statLabel}>Abonnés Payants</span>
                                    <strong style={{ ...s.statValue, color: themeColors.primary }}>
                                        {(Number(stats?.premium_users) || 0) + (Number(stats?.standard_users) || 0)}
                                    </strong>
                                </div>
                            </div>

                            {/* Bouton Toggle Filtre (Style Dashboard) */}
                            <button 
                                onClick={() => setIsFilterBarVisible(!isFilterBarVisible)}
                                style={{
                                    ...s.topButton,
                                    backgroundColor: isFilterBarVisible ? themeColors.accent : themeColors.primary,
                                    color: isFilterBarVisible ? themeColors.text : themeColors.textSecondary,
                                    marginBottom: '15px'
                                }}
                            >
                                {isFilterBarVisible ? 'Masquer les filtres' : (userFilter.description || userFilter.categorieId ? 'Filtres actifs 💡 (Afficher)' : 'Afficher les filtres')}
                            </button>

                            {isFilterBarVisible && (
                                <div style={s.filterBarContainer}>
                                    <AdminUserFilterBar 
                                        filter={userFilter}
                                        setFilter={setUserFilter}
                                        themeColors={themeColors}
                                        filteredCount={filteredUsers.length}
                                        totalCount={users.length}
                                    />
                                </div>
                            )}

                            {/* Barre d'actions groupées */}
                            {selectedUserIds.length > 0 && (
                                <div style={s.batchActionsBar}>
                                    <button style={{...s.topButton, backgroundColor: themeColors.success, color: '#fff'}} onClick={handleBatchUpdatePrice}>
                                        Modifier le tarif ({selectedUserIds.length})
                                    </button>
                                    <button style={{...s.topButton, backgroundColor: themeColors.defaultButton, color: themeColors.textSecondary}} onClick={() => setSelectedUserIds([])}>
                                        Annuler
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Tableau avec Scroll et Colonnes souhaitées */}
                        <div style={s.scrollContainer}>
                            <table style={{...s.table, backgroundColor: themeColors.mainBackground}}>
                                <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                                    <tr style={{ backgroundColor: themeColors.background }}>
                                        <th style={{...s.center, width: '40px', borderBottom: `1px solid ${themeColors.inputBorder}`}}>
                                            <input type="checkbox" ref={selectAllRef} onChange={handleSelectAll} />
                                        </th>
                                        <th style={{...s.left, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Nom</th>
                                        <th style={{...s.left, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Email</th>
                                        <th style={{...s.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Vérifié</th>
                                        <th style={{...s.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Tarif Mensuel</th>
                                        <th style={{...s.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Plan Actuel</th>
                                        <th style={{...s.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Maintenance</th>
                                        <th style={{...s.center, borderBottom: `1px solid ${themeColors.inputBorder}`}}>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.map(u => {
                                        const expiryDate = u.support_access_expires ? new Date(u.support_access_expires) : null;
                                        const isSupportActive = expiryDate && expiryDate > new Date();

                                        return (
                                            <tr key={u.id} 
                                                style={{
                                                    backgroundColor: selectedUserIds.includes(u.id) ? themeColors.navActive : 'transparent',
                                                    cursor: 'pointer'
                                                }}
                                                onClick={() => handleSelectUser(u.id)}
                                            >
                                                <td style={s.center} onClick={(e) => e.stopPropagation()}>
                                                    <input type="checkbox" checked={selectedUserIds.includes(u.id)} onChange={() => handleSelectUser(u.id)} />
                                                </td>
                                                <td style={s.left}><strong>{u.name}</strong></td>
                                                <td style={s.left} onClick={(e) => e.stopPropagation()}>
                                                    {u.email} <FaEdit onClick={() => handleUpdateEmail(u.id, u.email)} style={s.editIcon} title="Modifier l'email" />
                                                </td>
                                                <td style={s.center}>
                                                    {u.is_verified ? <FaCheckCircle color="green" /> : <FaTimesCircle color="red" />}
                                                </td>
                                                <td style={s.center}>
                                                    <strong>{formatCurrency(u.monthly_price)} €</strong>
                                                </td>
                                                <td style={s.center} onClick={(e) => e.stopPropagation()}>
                                                    <select 
                                                        value={u.plan_name} 
                                                        onChange={(e) => updatePlan(u.id, e.target.value)}
                                                        style={s.select}
                                                    >
                                                        <option value="TRIAL">TRIAL</option>
                                                        <option value="STANDARD">STANDARD</option>
                                                        <option value="PREMIUM">PREMIUM</option>
                                                    </select>
                                                </td>
                                                <td style={s.center}>
                                                    {isSupportActive ? (
                                                        <div style={{ color: '#27ae60', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
                                                            <MdSettingsInputAntenna className="blink" title="Support ouvert" />
                                                            <span style={{ fontSize: '0.7em', fontWeight: 'bold' }}>OUVERT</span>
                                                        </div>
                                                    ) : (
                                                        <span style={{ color: '#ccc', fontSize: '0.7em' }}>FERMÉ</span>
                                                    )}
                                                </td>

                                                <td style={s.center} onClick={(e) => e.stopPropagation()}>
                                                    <button 
                                                        onClick={() => handleLogAs(u.id)} 
                                                        style={{ 
                                                            ...s.actionBtn, 
                                                            color: isSupportActive ? themeColors.primary : '#ccc',
                                                            cursor: isSupportActive ? 'pointer' : 'not-allowed'
                                                        }} 
                                                        disabled={!isSupportActive}
                                                        title={isSupportActive ? "Se connecter en tant que..." : "L'utilisateur doit activer l'assistance"}
                                                    >
                                                        <FaKey />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {activeSubTab === 'stats' && (
                    <div style={s.tabContentContainer}>
                        <h2 style={{ color: themeColors.secondary, marginBottom: '25px' }}>Vue d'ensemble du système</h2>
                        
                        <div style={s.statsGrid}>
                            <div style={{ ...s.statCard, borderLeft: `5px solid ${themeColors.primary}` }}>
                                <span style={s.statLabel}>Utilisateurs Totaux</span>
                                <strong style={s.statValue}>{stats?.total_users || 0}</strong>
                            </div>
                            <div style={{ ...s.statCard, borderLeft: `5px solid #27ae60` }}>
                                <span style={s.statLabel}>Revenus Mensuels Est. (MRR)</span>
                                <strong style={s.statValue}>{stats?.estimated_mrr || 0} €</strong>
                            </div>
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                            <div style={s.tableWrapper}>
                                <h4 style={{ padding: '20px', margin: 0, backgroundColor: '#fafafa' }}>Répartition des Plans</h4>
                                <div style={{ padding: '20px' }}>
                                    {[
                                        { label: 'Premium', count: stats?.premium_users, color: themeColors.primary },
                                        { label: 'Standard', count: stats?.standard_users, color: themeColors.secondary },
                                        { label: 'Essai', count: stats?.trial_users, color: '#95a5a6' }
                                    ].map(p => (
                                        <div key={p.label} style={s.progressRow}>
                                            <span>{p.label} ({p.count || 0})</span>
                                            <div style={s.progressBar}>
                                                <div style={{ 
                                                    ...s.progressFill, 
                                                    width: `${(p.count / stats?.total_users) * 100 || 0}%`, 
                                                    backgroundColor: p.color 
                                                }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div style={s.tableWrapper}>
                                <h4 style={{ padding: '20px', margin: 0, backgroundColor: '#fafafa' }}>Dernières Inscriptions</h4>
                                <div style={{ padding: '10px 20px' }}>
                                    {Array.isArray(users) && users.slice(0, 5).map(u => (
                                        <div key={u.id} style={{ padding: '10px 0', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between' }}>
                                            <span>{u.name}</span>
                                            <span style={{ fontSize: '0.85em', color: '#666' }}>{u.plan_name}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeSubTab === 'config' && (
                    <div style={s.tabContentContainer}>
                        <h2 style={{ color: themeColors.secondary, marginBottom: '25px' }}>Configuration du Système</h2>
                        <div style={s.tableWrapper}>
                            <table style={s.table}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                                        <th style={s.th}>Paramètre</th>
                                        <th style={s.th}>Valeur</th>
                                        <th style={s.th}>Description</th>
                                        <th style={s.th}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {systemConfig.map(cfg => (
                                        <tr key={cfg.key} style={s.tr}>
                                            <td style={s.td}><code>{cfg.key}</code></td>
                                            <td style={s.td}>
                                                <input 
                                                    style={s.select} 
                                                    defaultValue={cfg.value} 
                                                    id={`input-${cfg.key}`}
                                                />
                                            </td>
                                            <td style={{ ...s.td, fontSize: '0.85em', color: '#666' }}>{cfg.description}</td>
                                            <td style={s.td}>
                                                <button 
                                                    onClick={() => {
                                                        const val = document.getElementById(`input-${cfg.key}`).value;
                                                        const apply = document.getElementById(`apply-${cfg.key}`)?.checked || false;
                                                        handleSaveConfig(cfg.key, val, apply);
                                                    }}
                                                    style={{ ...s.actionBtn, color: '#27ae60' }}
                                                >
                                                    Sauvegarder
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
                {isWaitingForUser && (
                    <div style={s.overlay}>
                        <div style={s.modal}>
                            <div className="support-blink" style={{ fontSize: '3rem', marginBottom: '20px' }}>⏳</div>
                            <h3>En attente de l'utilisateur...</h3>
                            <p>Veuillez ne pas fermer cette page. La session s'ouvrira dès que l'utilisateur aura accepté.</p>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
}

const s = {
    adminLayout: { display: "flex", height: "calc(100vh - 60px)", overflow: "hidden" },
    sidebar: { width: "220px", padding: "20px 0", overflowY: "auto" },
    navList: { listStyle: "none", padding: 0, margin: "10px 0" },
    navItem: { padding: "12px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: "10px", fontSize: "0.95em", transition: "0.2s" },
    navActive: (colors) => ({ padding: "12px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: "10px", backgroundColor: colors.navActive, fontWeight: "bold", fontSize: "0.95em" }),
    mainContent: { flex: 1, display: "flex", flexDirection: "column", padding: "20px", overflow: "hidden", backgroundColor: '#f5f7fa' },
    tabContentContainer: { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 },
    fixedContent: { flexShrink: 0 },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '20px' },
    statCard: { backgroundColor: '#fff', padding: '15px 20px', borderRadius: '12px', boxShadow: '0 2px 10px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column' },
    statLabel: { fontSize: '0.85em', color: '#666', marginBottom: '5px' },
    statValue: { fontSize: '1.5em', fontWeight: 'bold' },
    scrollContainer: { flex: 1, overflowY: 'auto', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#fff' },
    table: { width: "100%", borderCollapse: "collapse" },
    tableWrapper: { backgroundColor: '#fff', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' },
    th: { padding: '12px 15px', textAlign: 'left', fontSize: '0.8em', textTransform: 'uppercase', letterSpacing: '0.5px' },
    td: { padding: '12px 15px', borderBottom: '1px solid #f0f0f0', fontSize: '0.9em' },
    left: { textAlign: 'left', padding: '12px 15px' },
    center: { textAlign: 'center', padding: '12px 15px' },
    topButton: { border: "none", padding: "10px 15px", borderRadius: "4px", cursor: "pointer", fontWeight: "bold", transition: "0.2s" },
    batchActionsBar: { display: 'flex', gap: '10px', padding: '10px', backgroundColor: '#fff', borderRadius: '8px', marginBottom: '15px', border: '1px solid #ddd', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' },
    select: { padding: '6px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '0.9em', outline: 'none' },
    actionBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#3498db', fontSize: '1.1em' },
    editIcon: { cursor: 'pointer', color: '#95a5a6', marginLeft: '5px', fontSize: '0.8em' },
    input: { width: '100%', padding: "8px 12px", fontSize: "14px", borderRadius: "6px", border: "1px solid #ddd", backgroundColor: "#fff", boxSizing: 'border-box' },
    filterBar: { backgroundColor: '#fff', padding: '20px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' },
    filterLabel: { display: 'block', fontSize: '0.75em', fontWeight: 'bold', color: '#888', marginBottom: '5px', textTransform: 'uppercase' },
    resetButton: { backgroundColor: '#95a5a6', color: '#fff', border: 'none', padding: '10px 15px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', height: '38px' },
    filterBarContainer: { marginBottom: '15px' },
    progressRow: { marginBottom: '15px', display: 'flex', flexDirection: 'column', gap: '5px' },
    progressBar: { width: '100%', height: '8px', backgroundColor: '#eee', borderRadius: '4px', overflow: 'hidden' },
    progressFill: { height: '100%', transition: 'width 0.5s ease-out' },
};